import classNames from 'classnames';
import get from 'lodash/get';
import { bool, func, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import { compose } from 'redux';
import {
  Button,
  FieldCurrencyInput,
  FieldDateRangeInput,
  FieldTextInput,
  Form,
  ModalInMobile,
  PrimaryButton,
} from '../../components';
import config from '../../config';
import { formatMoney } from '../../util/currency';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  bookingDatesMinimum,
  bookingDatesRequired,
  composeValidators,
  maxValue,
  minValue,
  moneySubUnitAmountAtLeast,
  required,
} from '../../util/validators';

import omit from 'lodash/omit';
import moment from 'moment';
import { isInclusivelyAfterDay, isInclusivelyBeforeDay, isSameDay } from 'react-dates';
import { withRouter } from 'react-router-dom';
import { isDayNotAbleToBook } from '../../components/FieldDateRangeInput/DateRangeInput.helpers';
import {
  TRANSITION_DECLINE_OFFER,
  TRANSITION_ENQUIRE,
  TRANSITION_REQUEST_OFFER,
  TRANSITION_RE_OFFER,
} from '../../util/customTransaction/mainRentalTransaction';
import { ensureListing } from '../../util/data';
import { END_DATE, START_DATE, dateFromAPIToLocalNoon, getDateForEmail } from '../../util/dates';
import { LISTING_STATE_CLOSED } from '../../util/types';
import { parse, stringify } from '../../util/urlHelpers';
import css from './OfferForm.css';

const MODAL_BREAKPOINT = 1023;
const identity = v => v;

const { Money } = sdkTypes;

const OfferFormComponent = props => {
  const [focusedInput, setFocusInput] = useState(null);
  const [maxSeatShouldType, setMaxSeatShouldType] = useState(null);
  const [seatInpDisabled, setSeatInpDisabled] = useState(true);
  const [bookedDays, setBookDays] = useState([]);
  const { transaction, timeSlots } = props;

  const closeBookModal = (history, location) => {
    const { pathname, search, state } = location;
    const searchParams = omit(parse(search), 'book');
    const searchString = `?${stringify(searchParams)}`;
    history.push(`${pathname}${searchString}`, state);
  };

  const openBookModal = (isClosed, history, location) => {
    if (isClosed) {
      window.scrollTo(0, 0);
    } else {
      const { pathname, search, state } = location;
      const searchString = `?${stringify({ ...parse(search), book: true })}`;
      history.push(`${pathname}${searchString}`, state);
    }
  };

  const handleOffer = values => {
    const { price, bookingDates, seats } = values;
    const { startDate, endDate } = bookingDates || {};
    const { transaction, onTransit, history, location } = props;
    const lastTransition = transaction.attributes.lastTransition;
    if (!startDate) {
      setFocusInput(START_DATE);
    } else if (!endDate) {
      setFocusInput(END_DATE);
    } else {
      let transition = null;
      if (lastTransition === TRANSITION_ENQUIRE) {
        transition = TRANSITION_REQUEST_OFFER;
      } else {
        transition = TRANSITION_RE_OFFER;
      }

      if (price instanceof Money && transition && onTransit) {
        const params = {
          negotiatedTotal: price,
          protectedData: {
            offerStartDate: startDate.getTime(),
            offerEndDate: endDate.getTime(),
            offerMailStartDate: getDateForEmail(startDate),
            offerMailEndDate: getDateForEmail(endDate),
            seats: parseInt(seats),
          },
        };
        onTransit(transaction.id, transition, params).then(() => closeBookModal(history, location));
      }
    }
  };

  const handleOnChange = formValues => {
    const { onTimeSlotsRequest } = props;
    const { startDate, endDate } =
      formValues.values && formValues.values.bookingDates ? formValues.values.bookingDates : {};

    const { seats } = formValues.values || {};

    const listingId = props.listing.id.uuid;
    const hasSeatData = typeof seats !== 'undefined';

    if ((!startDate || !endDate) && !seatInpDisabled) {
      setSeatInpDisabled(true);
    }

    const utcFormattedStartDate = moment(startDate).utc();
    const utcFormattedEndDate = moment(endDate).utc();
    utcFormattedStartDate.set('hour', 0);
    utcFormattedEndDate.set('hour', 0);

    if (startDate && endDate && !hasSeatData) {
      // Fetch timeSlot in this duration to get the minimum/maximum seat available
      const params = {
        listingId,
        start: utcFormattedStartDate.toDate(),
        end: utcFormattedEndDate.toDate(),
      };

      onTimeSlotsRequest(params).then(response => {
        const minSeat = response.reduce((acc, val) => {
          if (acc === null || acc > val.attributes.seats) {
            acc = val.attributes.seats;
            return acc;
          } else return acc;
        }, null);

        setMaxSeatShouldType(minSeat);
        setSeatInpDisabled(false);
      });
    }
  };

  const onFocusedInputChange = focusedInput => {
    setFocusInput(focusedInput);
  };

  const getBookedDays = () => {
    const { timeSlots } = props;

    if (!timeSlots) {
      return [];
    }

    const result = [];

    const today = dateFromAPIToLocalNoon(
      moment()
        .utc()
        .startOf('day')
        .toDate()
    );
    const firstAvailableTimeslot = dateFromAPIToLocalNoon(timeSlots[0].attributes.start);
    const diffFromToday = moment(firstAvailableTimeslot).diff(moment(today), 'days');

    if (diffFromToday > 0) {
      for (let t = 0; t < diffFromToday; t++) {
        result.push(
          moment(today)
            .add(t, 'day')
            .toDate()
        );
      }
    }

    for (let i = 0; i < timeSlots.length - 1; i++) {
      const before = dateFromAPIToLocalNoon(timeSlots[i].attributes.start);
      const after = dateFromAPIToLocalNoon(timeSlots[i + 1].attributes.start);

      const diff = moment(after).diff(moment(before), 'days');

      if (diff === 1) {
        continue;
      }

      for (let t = 1; t < diff; t++) {
        result.push(
          moment(before)
            .add(t, 'day')
            .toDate()
        );
      }
    }

    return result;
  };
  useEffect(() => {
    setBookDays([...getBookedDays()]);
  }, [timeSlots]);

  const { payoutTotal, protectedData } = transaction.attributes;
  const { offerStartDate, offerEndDate, seats } = protectedData;
  const bookingDates =
    offerStartDate && offerEndDate
      ? {
          startDate: new Date(offerStartDate),
          endDate: new Date(offerEndDate),
        }
      : null;

  return (
    <FinalForm
      {...props}
      initialValues={{ seats: seats, price: payoutTotal, bookingDates }}
      onSubmit={() => false}
      render={fieldRenderProps => {
        const {
          className,
          rootClassName,
          handleSubmit,
          invalid,
          submitButtonWrapperClassName,
          intl,
          isProvider,
          disabled,
          transaction,
          transitInProgress,
          transitError,
          values,
          onManageDisableScrolling,
          history,
          location,
          listing,
          unitType,
          timeSlots,
          fetchTimeSlotsError,
          fetchLineItemsInProgress,
          startDatePlaceholder,
          endDatePlaceholder,
          formId,
          form,
          pristine,
        } = fieldRenderProps;

        const currentListing = ensureListing(listing);
        const { publicData } = currentListing.attributes;
        const { startDate, endDate } = values && values.bookingDates ? values.bookingDates : {};
        const { minRent, pricingType } = get(listing, 'attributes.publicData', {});
        const classes = classNames(rootClassName || css.root, className);
        const submitButtonWrapper = classNames(
          css.submitButtonWrapper,
          submitButtonWrapperClassName
        );

        const isBook = !!parse(location.search).book;
        const hasListingState = !!listing.attributes.state;
        const isClosed = hasListingState && listing.attributes.state === LISTING_STATE_CLOSED;

        const lastTransition = transaction.attributes.lastTransition;

        const submitInProgress = transitInProgress;
        const submitDisabled = invalid || transitInProgress || disabled || pristine;

        const priceRequired = required(
          intl.formatMessage({
            id: 'OfferForm.priceRequired',
          })
        );
        const minPrice = new Money(config.listingMinimumPriceSubUnits, config.currency);
        const minPriceRequired = moneySubUnitAmountAtLeast(
          intl.formatMessage(
            {
              id: 'OfferForm.priceTooLow',
            },
            {
              minPrice: formatMoney(intl, minPrice),
            }
          ),
          config.listingMinimumPriceSubUnits
        );
        const priceValidators = config.listingMinimumPriceSubUnits
          ? composeValidators(priceRequired, minPriceRequired)
          : priceRequired;

        const errorMessage = transitError ? intl.formatMessage({ id: 'OfferForm.error' }) : null;

        const { payoutTotal } = transaction.attributes;

        const offerPrice = payoutTotal
          ? intl.formatMessage(
              { id: 'OfferForm.offerPrice' },
              { price: formatMoney(intl, payoutTotal) }
            )
          : intl.formatMessage({ id: 'OfferForm.notOffer' });

        const priceLabel = intl.formatMessage({ id: 'OfferForm.priceLabel' });
        const pricePlaceholder = intl.formatMessage({ id: 'OfferForm.pricePlaceholder' });
        const showOfferSection = isProvider;

        const offerButtonText =
          lastTransition === TRANSITION_DECLINE_OFFER || lastTransition === TRANSITION_RE_OFFER
            ? intl.formatMessage({ id: 'OfferForm.reOffer' })
            : intl.formatMessage({ id: 'OfferForm.giveOffer' });

        const bookingStartLabel = intl.formatMessage({
          id: 'BookingDatesForm.bookingStartTitle',
        });
        const bookingEndLabel = intl.formatMessage({
          id: 'BookingDatesForm.bookingEndTitle',
        });
        const requiredMessage = intl.formatMessage({
          id: 'BookingDatesForm.requiredDate',
        });
        const startDateErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.invalidStartDate',
        });
        const endDateErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.invalidEndDate',
        });
        const dateFormatOptions = {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        };

        const now = moment();
        const today = now.startOf('day').toDate();
        const tomorrow = now
          .startOf('day')
          .add(1, 'days')
          .toDate();

        const startDatePlaceholderText =
          startDatePlaceholder || intl.formatDate(today, dateFormatOptions);
        const endDatePlaceholderText =
          endDatePlaceholder || intl.formatDate(tomorrow, dateFormatOptions);

        const timeSlotsError = fetchTimeSlotsError ? (
          <p className={css.sideBarError}>
            <FormattedMessage id="BookingDatesForm.timeSlotsError" />
          </p>
        ) : null;

        const seatLabel = intl.formatMessage({ id: 'BookingDatesForm.seat' });
        const seatRequired = required(
          intl.formatMessage({
            id: 'BookingDatesForm.seatRequired',
          })
        );
        const minSeatRequired = minValue(
          intl.formatMessage(
            {
              id: 'BookingDatesForm.minSeat',
            },
            { minSeat: config.listingMinimumSeats }
          ),
          config.listingMinimumSeats
        );
        const maxSeatRequired = maxValue(
          intl.formatMessage(
            {
              id: 'BookingDatesForm.maxSeat',
            },
            { maxSeat: maxSeatShouldType }
          ),
          maxSeatShouldType
        );
        const seatValidators =
          maxSeatShouldType !== null
            ? composeValidators(seatRequired, minSeatRequired, maxSeatRequired)
            : composeValidators(seatRequired, minSeatRequired);
        const seatClasses = classNames(css.seat, {
          [css.seatInpDisabled]: seatInpDisabled,
        });
        const endOfRange = config.dayCountAvailableForBooking - 1;
        const isOutsideRange = day =>
          startDate
            ? !isInclusivelyBeforeDay(
                day,
                moment(startDate)
                  .clone()
                  .add(endOfRange, 'days')
              ) || !isInclusivelyAfterDay(day, moment())
            : null;

        const isDayBlocked = startDate
          ? day => {
              return bookedDays.find(days => {
                const localStartDate = dateFromAPIToLocalNoon(days);
                return isSameDay(day, moment(localStartDate));
              });
            }
          : null;
        const isDayHighlighted = day => isDayNotAbleToBook(timeSlots, minRent, pricingType)(day);

        const bookingDatePeriodErrorMessage = intl.formatMessage(
          {
            id: 'FieldDateRangeInput.bookingDatePeriodError',
          },
          {
            minRent: `${publicData.minRent} ${publicData.pricingType}${
              publicData.minRent > 1 ? 's' : ''
            }`,
          }
        );
        return (
          <div className={classes}>
            <ModalInMobile
              containerClassName={css.modalContainer}
              id="BookingDatesFormInModal"
              isModalOpenOnMobile={isBook}
              onClose={() => closeBookModal(history, location)}
              showAsModalMaxWidth={MODAL_BREAKPOINT}
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <Form className={css.form} onSubmit={handleSubmit}>
                {timeSlotsError}

                <FormSpy
                  subscription={{ values: true }}
                  onChange={newData => {
                    const { startDate: newStartDate, endDate: newEndDate } =
                      (newData && newData.values && newData.values.bookingDates) || {};

                    if (
                      newData.values.bookingDates &&
                      newStartDate &&
                      newEndDate &&
                      startDate &&
                      endDate &&
                      (newStartDate.toString() !== startDate.toString() ||
                        newEndDate.toString() !== endDate.toString())
                    ) {
                      form.change('seats', undefined);
                    }

                    handleOnChange(newData);
                  }}
                />

                <div className={css.offerPrice}>{offerPrice}</div>
                {showOfferSection ? (
                  <>
                    <FieldCurrencyInput
                      id="price"
                      name="price"
                      className={css.offerPrice}
                      label={priceLabel}
                      placeholder={pricePlaceholder}
                      currencyConfig={config.currencyConfig}
                      validate={priceValidators}
                    />

                    <FieldDateRangeInput
                      className={css.bookingDates}
                      name="bookingDates"
                      unitType={unitType}
                      startDateId={`${formId}.bookingStartDate`}
                      startDateLabel={bookingStartLabel}
                      startDatePlaceholderText={startDatePlaceholderText}
                      endDateId={`${formId}.bookingEndDate`}
                      endDateLabel={bookingEndLabel}
                      endDatePlaceholderText={endDatePlaceholderText}
                      focusedInput={focusedInput}
                      onFocusedInputChange={onFocusedInputChange}
                      format={identity}
                      timeSlots={timeSlots}
                      useMobileMargins
                      validate={composeValidators(
                        required(requiredMessage),
                        bookingDatesRequired(startDateErrorMessage, endDateErrorMessage),
                        bookingDatesMinimum(bookingDatePeriodErrorMessage, minRent, pricingType)
                      )}
                      disabled={fetchLineItemsInProgress}
                      isOutsideRange={isOutsideRange}
                      isDayHighlighted={isDayHighlighted}
                      isDayBlocked={isDayBlocked}
                    />

                    <FieldTextInput
                      id="seats"
                      name="seats"
                      className={seatClasses}
                      type="number"
                      label={seatLabel}
                      validate={seatValidators}
                      disabled={seatInpDisabled}
                      step="1"
                    />

                    <div className={submitButtonWrapper}>
                      <div className={css.deliveryDescription}>
                        <FormattedMessage id="OfferForm.deliveryDescription" />
                      </div>
                      <PrimaryButton
                        type="button"
                        disabled={submitDisabled}
                        inProgress={submitInProgress}
                        onClick={() => handleOffer(values)}
                      >
                        {offerButtonText}
                      </PrimaryButton>
                    </div>

                    <div className={css.errorMessage}>{errorMessage}</div>
                  </>
                ) : null}
              </Form>
            </ModalInMobile>
            <div className={css.openBookingForm}>
              <div className={css.priceOffered}>{offerPrice}</div>
              <Button
                rootClassName={css.bookButton}
                onClick={() => openBookModal(isClosed, history, location)}
              >
                <FormattedMessage id="OfferForm.ctaButtonMessage" />
              </Button>
            </div>
          </div>
        );
      }}
    />
  );
};

OfferFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  inProgress: false,
};

OfferFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,
  onTimeSlotsRequest: func.isRequired,

  inProgress: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OfferForm = compose(withRouter, injectIntl)(OfferFormComponent);

export default OfferForm;
